import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, signal, Type } from '@angular/core';
import { ServiceStateEnum } from '@mwe/constants';
import { BackendProcessStore, StaticContentService, StepInterface, StepStateInterface } from '@mwe/services';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { TranslateDirective, TranslateService } from '@ngx-translate/core';
import { NgClass, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { NextStepsComponent } from '../../next-steps/next-steps.component';
import { AddStaticImagePathPipe } from '@mwe/ui/pipes';
import { BaseProductAddModule } from '@mwe/features/product-add';
import { InlineNotificationComponent } from '../../inline-notification/inline-notification.component';

type StepRepresentation = { step: StepInterface; state?: StepStateInterface };
export type InfoStep = Pick<StepInterface, 'title' | 'label'> & Partial<Pick<StepInterface, 'translationParams'>>;

@Component({
  selector: 'mwe-backend-process-presentation-component',
  templateUrl: './backend-process-presentation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BasicCardComponent,
    TranslateDirective,
    NgTemplateOutlet,
    NextStepsComponent,
    NgComponentOutlet,
    NgClass,
    AddStaticImagePathPipe,
    BaseProductAddModule,
    InlineNotificationComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BackendProcessPresentationComponent {
  processTitle = input<string>('');
  infoSteps = input<InfoStep[]>([]);
  infoTitleKey = input<string>('summary.whatNext');
  errorTitleKey = input<string>('process.default.error.title');
  errorMsgKey = input<string>('process.default.error.message');
  ctaLabelKey = input<string>(undefined);
  ctaOnClick = input<string>(undefined);
  errorMsgRetryKey = input<string>('process.default.error.message_retry');
  workInProgressMsgKey = input<string>('process.default.loading.title');
  workInProgressSubtextKey = input<string>('');
  successMsgKey = input<string>('process.default.success.title');
  successSubtextKey = input<string>('process.default.success.message');
  shouldHideKeysIfFirstStepIsError = input<boolean>(true);
  alertPlaceholders = signal<Map<string, string>>(undefined);

  /**
   * Optional component to render the steps
   * Will get step: StepInterface and state: StepStateInterface as inputs
   */
  stepRenderer = signal<Type<unknown>>(null);

  process = inject(BackendProcessStore);
  private readonly staticContentService = inject(StaticContentService);
  private readonly translateService = inject(TranslateService);

  steps = computed<StepRepresentation[]>(() => {
    const steps = Array.from(this.process.steps().entries());
    return steps.map(([step, state]) => ({ step, state }));
  });

  isFirstStepError = computed(() => {
    const steps = this.steps();
    if (steps.length === 0) {
      return false;
    }
    const firstStepState = steps[0].state;
    if (!firstStepState) {
      return false;
    }
    return firstStepState.state === ServiceStateEnum.ERROR || firstStepState.state === ServiceStateEnum.FAILED;
  });

  showLoadingState = computed(() => {
    const processState = this.process.viewState();
    const processInALoadingState = processState === ServiceStateEnum.INIT || processState === ServiceStateEnum.LOADING;
    return processInALoadingState;
  });

  showErrorState = computed(
    () =>
      !this.showLoadingState() &&
      (this.process.viewState() === ServiceStateEnum.ERROR || this.process.viewState() === ServiceStateEnum.FAILED),
  );

  showSuccessState = computed(() => !this.showLoadingState() && this.process.viewState() === ServiceStateEnum.SUCCESS);

  retryAllowed = computed(() => this.process.retryAllowed());

  getIconClassForStep(item: StepRepresentation): string | undefined {
    const { step, state } = item;
    const icon = step.iconMap ? step.iconMap.get(state.state) : undefined;
    const cls = step.classMap ? step.classMap.get(state.state) : undefined;
    return icon ? `fas ${icon} ${cls ?? ''}` : undefined;
  }

  getTitleForStep(item: StepRepresentation): string {
    const { step, state } = item;
    const defaultTitle = step.title;
    const stateTitle = step.textMap.get(state.state)?.title;
    return stateTitle ?? defaultTitle;
  }

  getLabelsForStep(item: StepRepresentation): string[] {
    const { step, state } = item;
    const defaultLabel = step?.label ? (Array.isArray(step.label) ? step.label : [step.label]) : undefined;
    let stateLabel = step.textMap.get(state.state)?.label;
    stateLabel = stateLabel ? (Array.isArray(stateLabel) ? stateLabel : [stateLabel]) : undefined;
    return stateLabel ?? defaultLabel;
  }

  getImagePath() {
    return this.staticContentService.getImagePath();
  }

  handleCTAClick(): void {
    const urlKey = this.ctaOnClick();
    if (urlKey) {
      const url = this.translateService.instant(urlKey);
      window.open(url, '_blank', 'noopener');
    }
  }
}
